import { useQuery } from '@tanstack/react-query';

import type { PricingDetails } from '~/interfaces/subscriptions';

export const pricingDetailsKeys = {
  all: ['pricing-details'] as const,
  byParams: (offerId?: string, email?: string) => ['pricing-details', offerId, email] as const,
};

const fetchPricingDetails = async (offerId?: string, email?: string): Promise<PricingDetails> => {
  const params = new URLSearchParams();
  if (offerId) params.append('offer_id', offerId);
  if (email) params.append('email', email);
  const searchString = params.size > 0 ? `?${params.toString()}` : '';

  const response = await fetch(`/pricing_details${searchString}`, {
    headers: { 'Sec-Fetch-Dest': 'empty' },
  });
  if (!response.ok) throw new Error('Failed to fetch pricing details');
  const data: PricingDetails = await response.json();
  return data;
};

interface Props {
  enabled?: boolean;
}

const usePricingDetails = ({ enabled = true }: Props) => {
  const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const offerId = searchParams.get('offer_id') ?? undefined;
  const email = searchParams.get('email') ?? undefined;

  return useQuery({
    queryKey: pricingDetailsKeys.byParams(offerId, email),
    queryFn: () => fetchPricingDetails(offerId, email),
    enabled,
    staleTime: 30_000,
  });
};

export default usePricingDetails;
